import React, { FC, useRef, useState, useContext, useEffect, useCallback, memo } from 'react'
import { CarouselHandle, GalleryLayout } from 'react-components'
import Gallery from './gallery'
import { GalleryWrapper, ThumnailImageWrapper, StyledSlider, StyledCarouselProvider } from './styles'
import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'
import { Slide, ButtonBack, ButtonNext } from 'pure-react-carousel'
import ImageLoadingComponent from '../../app-components/image-loading-component'
import { ThemeContext } from 'styled-components'
import ThumbnailBack from './thumbnail-back'
import ThumbnailNext from './thumbnail-next'
import { GalleryImages } from '../../../models/property-details-page/gallery-model'
import { FormattedBookingTypeData } from '../../../models/property-details-page/booking-type-model'
import { SearchbarFieldsReturn, UpdateSearchBarTransport } from '../hooks/use-searchbar'
import GalleryHeader from './gallery-header'
import GalleryFooter from './gallery-footer'
import { FormattedLivePricingDataTransport } from '../../../models/pricing/live-pricing-formatter'
import InquiryContactDetailsTransport from '../../../transports/inquiry-contact-details-transport'
import UserDetailsModel from '../../../models/user/user-details-model'
import AnalyticsService from '../../../services/analytics-service'
import { pdpPageActions } from '../../../services/analytics-service/actions'
import { usePropertyDetailsContext } from '../../../hooks/property-details'
import { getCategory } from '../../../utils/category-utils'
import { getBasePath } from '../../../utils/miscellaneous-utils'
import { useABVariant } from '../../../utils/hooks'
import { AB_KEY, AB_VARIANT_VERSION } from '../../../common/constants'

type GalleryImageClickInfo = {
    imageIndex: number
    layout: GalleryLayout
}

type GalleryProps = {
    imageBorderRadius?: string
    imageBorder?: string
    showThumbnail?: boolean
    layout?: GalleryLayout
    images: GalleryImages[]
    video?: GalleryImages
    bookingType: FormattedBookingTypeData | null
    propertyName: string
    searchbar: SearchbarFieldsReturn
    onSelectDatesButtonClick: () => void
    pricingDetails: FormattedLivePricingDataTransport | null
    inquiryContactDetails: InquiryContactDetailsTransport | null
    isPricingLoading: boolean
    isAvailabilityLoading: boolean
    pricingErrorCode: number | null
    updateSearchbar: UpdateSearchBarTransport
    availableDates: Date[] | null
    userDetails: UserDetailsModel | null
    petsAllowed: boolean
}

const DEFAULT_BORDER = '1px solid #ccc'
const DEFAULT_BORDER_RADIUS = '0'
const VISIBLE_SLIDE = 6

const ExposeGallery: FC<GalleryProps> = ({
    imageBorder = DEFAULT_BORDER,
    imageBorderRadius = DEFAULT_BORDER_RADIUS,
    showThumbnail = false,
    layout = GalleryLayout.CAROUSEL,
    images,
    video,
    bookingType,
    propertyName,
    searchbar,
    onSelectDatesButtonClick,
    ...restProps
}) => {
    const sliderRef = useRef<CarouselHandle | null>(null)
    const thumbnailContainerRef = useRef<HTMLDivElement | null>(null)
    const [currentIndex, setCurrentIndex] = useState(0)
    const [currentSlide, setCurrentSlide] = useState(0)
    const [visibleSlides, setVisibleSlides] = useState(VISIBLE_SLIDE)
    const theme = useContext(ThemeContext)
    const smallBorderRadius = theme.typography.border.radius.small
    const { isComboListing } = usePropertyDetailsContext()
    const { isProvidedVariantActive: isVirtualTourOverlayActive } = useABVariant(
        AB_KEY.VIRTUAL_TOUR_PDP,
        AB_VARIANT_VERSION.ONE,
    )

    const onGalleryImageClick = useCallback(
        (clickInfo: GalleryImageClickInfo) => {
            if (clickInfo.layout === GalleryLayout.GRID) {
                AnalyticsService.trackEvent({
                    category: getCategory(isComboListing),
                    action: pdpPageActions.GALLERY_MAIN_IMAGE_CLICKED,
                    label: `main_image_${clickInfo.imageIndex}`,
                })
            }
        },
        [isComboListing],
    )

    const onFullscreenImageClick = useCallback(
        (imageIndex: number) => {
            const clickedImage = images[imageIndex]
            if (clickedImage) {
                AnalyticsService.trackEvent({
                    category: getCategory(isComboListing),
                    action: pdpPageActions.GALLERY_FULLSCREEN_IMAGE_CLICKED,
                    label: `image_${imageIndex}_url_${clickedImage.src}`,
                })
            }
        },
        [images, isComboListing],
    )

    const onViewAllClick = useCallback(() => {
        if (layout === GalleryLayout.GRID) {
            AnalyticsService.trackEvent({
                category: getCategory(isComboListing),
                action: pdpPageActions.GALLERY_VIEW_ALL_CLICKED,
                label: 'view_all_overlay',
            })
        }
    }, [layout, isComboListing])

    const onFullScreenOpen = useCallback(() => {
        AnalyticsService.trackEvent({
            category: getCategory(isComboListing),
            action: pdpPageActions.GALLERY_FULLSCREEN_OPENED,
            label: 'fullscreen_modal_opened',
        })
    }, [isComboListing])

    const onOverlayOpen = useCallback(() => {
        AnalyticsService.trackEvent({
            category: getCategory(isComboListing),
            action: pdpPageActions.GALLERY_OVERLAY_OPENED,
            label: 'overlay_modal_opened',
        })
    }, [isComboListing])

    // Track when virtual tour video is available
    useEffect(() => {
        if (video) {
            AnalyticsService.trackEvent({
                category: getCategory(isComboListing),
                action: pdpPageActions.GALLERY_VIRTUAL_TOUR_AVAILABLE,
                label: `video_src_${video.src}`,
            })
        }
    }, [video, isComboListing])

    const calculateVisibleSlides = useCallback(() => {
        if (thumbnailContainerRef.current) {
            const containerWidth = thumbnailContainerRef.current.offsetWidth
            const thumbnailWidth = 110
            const newVisibleSlides = Math.floor(containerWidth / thumbnailWidth)
            setVisibleSlides(newVisibleSlides > 0 ? newVisibleSlides : VISIBLE_SLIDE)
        }
    }, [])

    useEffect(() => {
        calculateVisibleSlides()
        window.addEventListener('resize', calculateVisibleSlides)
        return () => window.removeEventListener('resize', calculateVisibleSlides)
    }, [calculateVisibleSlides])

    useEffect(() => {
        if (showThumbnail) {
            const startVisible = currentSlide
            const endVisible = currentSlide + visibleSlides - 1
            if (currentIndex < startVisible || currentIndex > endVisible) {
                setCurrentSlide(Math.max(0, currentIndex - Math.floor(visibleSlides / 2)))
            }
        }
    }, [currentIndex, currentSlide, visibleSlides, showThumbnail])

    const onSlideChange = useCallback(
        (index: number) => {
            if (showThumbnail) {
                setCurrentIndex(index)
            }
        },
        [showThumbnail],
    )

    const onThumbnailHover = useCallback((index: number) => {
        sliderRef.current?.goToSlide(index)
    }, [])

    const renderThumbnails = useCallback(() => {
        return images.map((image, index) => (
            <Slide key={`thumbnail_${image.caption}_${index}`} index={index}>
                <ThumnailImageWrapper isActive={index === currentIndex} onMouseEnter={() => onThumbnailHover(index)}>
                    <ImageLoadingComponent src={image.thumbnail} borderRadius={smallBorderRadius} />
                </ThumnailImageWrapper>
            </Slide>
        ))
    }, [images, currentIndex, onThumbnailHover, smallBorderRadius])

    const galleryIconPath = layout === GalleryLayout.GRID ? getBasePath('/assets/icons/gallery-icon.svg') : undefined

    return (
        <GalleryWrapper>
            <Gallery
                images={images}
                video={video}
                layout={layout}
                showArrows={false}
                skipFullScreen={isVirtualTourOverlayActive}
                ref={sliderRef}
                onSlideChange={onSlideChange}
                imageBorderRadius={imageBorderRadius}
                onGalleryImageClick={onGalleryImageClick}
                onViewAllClick={onViewAllClick}
                onFullscreenImageClick={onFullscreenImageClick}
                onFullScreenOpen={onFullScreenOpen}
                onOverlayOpen={onOverlayOpen}
                galleryIconSrc={galleryIconPath}
                header={({ onClose }) => (
                    <GalleryHeader
                        onClose={onClose}
                        bookingType={bookingType}
                        propertyName={propertyName}
                        searchbar={searchbar}
                        onSelectDatesButtonClick={onSelectDatesButtonClick}
                        {...restProps}
                    />
                )}
                footer={({ onClose }) => (
                    <GalleryFooter
                        bookingType={bookingType}
                        searchbar={searchbar}
                        onClose={onClose}
                        onSelectDatesButtonClick={onSelectDatesButtonClick}
                        {...restProps}
                    />
                )}
            />
            {showThumbnail && layout === GalleryLayout.CAROUSEL && (
                <div ref={thumbnailContainerRef}>
                    <StyledCarouselProvider
                        totalSlides={images.length}
                        visibleSlides={visibleSlides}
                        naturalSlideWidth={6.5}
                        naturalSlideHeight={5.5}
                        dragEnabled={false}
                        step={visibleSlides}
                        currentSlide={currentSlide}
                    >
                        <StyledSlider
                            onMouseEnter={() => sliderRef?.current?.stopAutoplay()}
                            onMouseLeave={() => sliderRef?.current?.resumeAutoplay()}
                        >
                            {renderThumbnails()}
                        </StyledSlider>
                        <ButtonBack>
                            <ThumbnailBack />
                        </ButtonBack>
                        <ButtonNext>
                            <ThumbnailNext numberOfSlides={images.length} visibleSlides={visibleSlides} />
                        </ButtonNext>
                    </StyledCarouselProvider>
                </div>
            )}
        </GalleryWrapper>
    )
}

export default memo(ExposeGallery)
